import service from '@/utils/https'
import { getExternalApiBaseUrl } from '@/lib/externalPageClient'

export interface PortageCompanyOption {
  id: string
  name: string
  city: string | null
  email: string
  status: string
  /** Source de vérité de la confirmation : false = société invitée pas encore confirmée. */
  onboarding_completed: boolean
}

export interface PortageContactOption {
  id: string
  first_name: string | null
  last_name: string | null
  email: string
  status: string
}

export interface PortageContact {
  id: string
  first_name: string | null
  last_name: string | null
  email: string
  status: string
  created_at?: string
  already_existed?: boolean
}

/**
 * Société de portage invitée localement (staged) : pas encore créée en DB. Créée par le backend
 * à la fin de l'onboarding freelance (payload `new_portage_company`).
 */
export interface StagedPortageInvite {
  name: string
  contact: { first_name: string | null; last_name: string | null; email: string }
}

export interface CreatePortageCompanyData {
  name: string
  contact: {
    first_name?: string
    last_name?: string
    email: string
  }
  siren_number?: string
  address?: string
  postal_code?: string
  city?: string
  country?: string
  vat_number?: string
}

export interface SuggestPortageContactData {
  first_name?: string
  last_name?: string
  email: string
}

export interface PortageCompany {
  id: string
  name: string
  email: string
  city: string | null
  status: string
  siren_number: string | null
  created_at: string
  already_existed?: boolean
  contact?: PortageContact | null
}

export interface PortageAffiliationSummary {
  id: string
  status: string
  portage_company_id: string
  portage_contact_id: string | null
  contact: PortageContact | null
}

/** Évite "Vincent Vincent Imhoff" quand le nom de famille contient déjà le prénom (autofill navigateur). */
export function normalizePortageContactNames(
  firstName?: string | null,
  lastName?: string | null
): { firstName: string | null; lastName: string | null } {
  const first = (firstName ?? '').trim()
  let last = (lastName ?? '').trim()

  if (first && last) {
    const firstLower = first.toLowerCase()
    const lastLower = last.toLowerCase()
    if (lastLower.startsWith(`${firstLower} `)) {
      last = last.slice(first.length).trim()
    } else if (lastLower === firstLower) {
      last = ''
    }
  }

  return {
    firstName: first || null,
    lastName: last || null,
  }
}

export function formatPortageContactLabel(contact: {
  first_name?: string | null
  last_name?: string | null
  email: string
}): string {
  const { firstName, lastName } = normalizePortageContactNames(
    contact.first_name,
    contact.last_name
  )
  const name = [firstName, lastName].filter(Boolean).join(' ').trim()
  return name || contact.email
}

/** Name + email parts for company info (two-line display). */
export function getPortageReferentDisplayParts(
  contact: {
    first_name?: string | null
    last_name?: string | null
    email?: string | null
  } | null
  | undefined,
  fallbackEmail?: string | null
): { name: string | null; email: string | null } {
  const email = (contact?.email || fallbackEmail || '').trim() || null
  if (!email) return { name: null, email: null }

  if (!contact?.email) {
    return { name: null, email }
  }

  const name = formatPortageContactLabel({
    first_name: contact.first_name,
    last_name: contact.last_name,
    email: contact.email,
  })

  if (name && name !== email) {
    return { name, email }
  }

  return { name: null, email }
}

/** Display for company info: "Prénom Nom / email" (name omitted when only email is known). */
export function formatPortageReferentDisplay(
  contact: {
    first_name?: string | null
    last_name?: string | null
    email?: string | null
  } | null
  | undefined,
  fallbackEmail?: string | null
): string | null {
  const { name, email } = getPortageReferentDisplayParts(contact, fallbackEmail)
  if (!email) return null
  if (name) return `${name} / ${email}`
  return email
}

export const portageService = {
  async getPortageCompanyOptions(search?: string): Promise<PortageCompanyOption[]> {
    const params: Record<string, string> = {}
    if (search) params.search = search
    const res = await service.get('/api/app/v1/portage-companies/options', { params })
    const data = res?.data ?? res
    if (data?.code === 200) {
      return (data.data?.items || []) as PortageCompanyOption[]
    }
    return [] as PortageCompanyOption[]
  },

  async getPortageContactOptions(
    portageCompanyId: string,
    search?: string
  ): Promise<PortageContactOption[]> {
    const params: Record<string, string> = {}
    if (search) params.search = search
    const res = await service.get(
      `/api/app/v1/portage-companies/${portageCompanyId}/contacts/options`,
      { params }
    )
    const data = res?.data ?? res
    if (data?.code === 200) {
      return (data.data?.items || []) as PortageContactOption[]
    }
    return [] as PortageContactOption[]
  },

  async createPortageCompany(data: CreatePortageCompanyData): Promise<PortageCompany> {
    const res = await service.post('/api/app/v1/portage-companies', data)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data as PortageCompany
    }
    throw new Error(responseData?.message || 'Failed to create portage company')
  },

  async suggestPortageContact(
    portageCompanyId: string,
    data: SuggestPortageContactData
  ): Promise<PortageContact> {
    const res = await service.post(
      `/api/app/v1/portage-companies/${portageCompanyId}/contacts/suggest`,
      data
    )
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data as PortageContact
    }
    throw new Error(responseData?.message || 'Failed to suggest portage contact')
  },

  async updatePendingAffiliationContact(
    portageCompanyId: string,
    data: { portage_contact_id: string; freelancer_id?: string }
  ): Promise<{
    affiliation_id: string
    portage_contact_id: string
    freelancer_id: string
    invitation_sent_at?: string
    skipped: boolean
  }> {
    try {
      const res = await service.patch(
        `/api/app/v1/portage-companies/${portageCompanyId}/affiliation-contact`,
        data,
        { hideMessageModal: true }
      )
      const responseData = res?.data ?? res
      if (responseData?.code === 200) {
        return responseData.data
      }
      throw new Error(responseData?.message || 'Failed to update portage contact')
    } catch (error: unknown) {
      const apiMessage =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data &&
        typeof (error.data as { message?: string }).message === 'string'
          ? (error.data as { message: string }).message
          : null
      throw new Error(
        apiMessage || (error instanceof Error ? error.message : 'Failed to update portage contact')
      )
    }
  },

  async resendPortageInvitation(
    id: string,
    options?: { freelancerId?: string }
  ): Promise<{ skipped: boolean; invitation_sent_at?: string }> {
    try {
      const body =
        options?.freelancerId && options.freelancerId.trim()
          ? { freelancer_id: options.freelancerId.trim() }
          : {}
      const res = await service.post(
        `/api/app/v1/portage-companies/${id}/resend-invitation`,
        body,
        { hideMessageModal: true }
      )
      const responseData = res?.data ?? res
      if (responseData?.code === 200) {
        return responseData.data as { skipped: boolean; invitation_sent_at?: string }
      }
      throw new Error(responseData?.message || 'Failed to resend invitation')
    } catch (error: unknown) {
      const apiMessage =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data &&
        typeof (error.data as { message?: string }).message === 'string'
          ? (error.data as { message: string }).message
          : null
      throw new Error(apiMessage || (error instanceof Error ? error.message : 'Failed to resend invitation'))
    }
  },

  async resendPortageMissionSignature(
    missionId: string,
    options?: { addendumId?: string }
  ): Promise<{ skipped: boolean }> {
    const body: Record<string, string> = {}
    if (options?.addendumId) {
      body.addendum_id = options.addendumId
    }
    const res = await service.post(
      `/api/app/v1/missions/${missionId}/resend-portage-signature`,
      body
    )
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data as { skipped: boolean }
    }
    throw new Error(responseData?.message || 'Failed to resend portage mission signature email')
  },

  async initiatePortageMissionSignature(params: {
    token: string
    signableType: 'mission_document' | 'master_agreement'
    signableId: string
    missionId: string
  }): Promise<{ signing_url: string; signature_request_id?: string }> {
    const res = await fetch(`${getExternalApiBaseUrl()}/api/app/v1/portage/public/mission/signature/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: params.token,
        signable_type: params.signableType,
        signable_id: params.signableId,
        mission_id: params.missionId,
        signer_role: 'freelancer',
      }),
    })
    let json: { message?: string; data?: { signing_url?: string; signature_request_id?: string; reason?: string } } =
      {}
    try {
      json = await res.json()
    } catch {
      throw Object.assign(new Error('Invalid response from server'), { status: res.status })
    }
    if (!res.ok) {
      throw Object.assign(
        new Error(json.message || 'Failed to initiate portage mission signature'),
        { status: res.status, response: { status: res.status, data: json } }
      )
    }
    const data = json.data as { signing_url?: string; signature_request_id?: string }
    if (!data?.signing_url) {
      throw new Error('Missing signing URL')
    }
    return {
      signing_url: data.signing_url,
      signature_request_id: data.signature_request_id,
    }
  },

  async rejectPortageMissionSignature(params: {
    token: string
    signableType: 'mission_document' | 'master_agreement'
    signableId: string
    missionId: string
    reason: string
  }): Promise<void> {
    const res = await fetch(`${getExternalApiBaseUrl()}/api/app/v1/portage/public/mission/signature/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: params.token,
        signable_type: params.signableType,
        signable_id: params.signableId,
        mission_id: params.missionId,
        reason: params.reason,
      }),
    })
    const json = await res.json()
    if (!res.ok) {
      throw new Error(json.message || 'Failed to reject portage mission document')
    }
  },
}
